/**
 * Register API + optional session write after a successful `register` response.
 *
 * Consumer map:
 *   - `postRegisterRequest` / `registerSuccessPersistToStore` → v1–v3 `RegisterSheetContent` (barrel `RegisterScreen`) / v2 inner `SignUpCredentialForm`
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import { apiPost } from '../../api/client';
import { endpoints } from '../../config/endpoints';
import type { User } from '../../../data/domains/profile';
import { getAuthStore } from '../store';
import { AUTH_TOKEN_KEY, CURRENT_USER_KEY } from '../session/keys';
import { setLoginJustHappened } from '../session/postLoginSignals';
import { scheduleFcmBackendSync } from '../../../platform/notifications/fcmBackendSync';

export async function postRegisterRequest(
  payload: Record<string, unknown>
): Promise<unknown> {
  return apiPost<unknown>(endpoints.register, payload);
}

export type RegisterSuccessPersistResult =
  | { ok: true; user: User }
  | { ok: false; message: string };

/**
 * Parses a successful `register` response, writes token + user to storage and zustand (when token present),
 * and returns the user for navigation (e.g. OTP) — same shape as v1 `RegisterScreen` success block.
 */
export async function registerSuccessPersistToStore(
  response: unknown
): Promise<RegisterSuccessPersistResult> {
  const r = response as {
    success?: boolean;
    message?: string;
    data?: { customer?: Record<string, unknown>; session_token?: string };
    customer?: Record<string, unknown>;
    session_token?: string;
  };
  if (!r || r.success !== true) {
    return { ok: false, message: String((r as { message?: string }).message || '') };
  }
  const data = r.data;
  const customerData = (data?.customer ?? r.customer) as Record<string, unknown> | undefined;
  const token_from_top = (data?.session_token ?? r.session_token) as string | undefined;
  if (!customerData || typeof customerData !== 'object') {
    return { ok: false, message: r.message || 'Invalid register response' };
  }
  const token = (customerData.session_token as string | undefined) ?? token_from_top;
  const customerWithVerification = {
    ...customerData,
    is_verified: (customerData.is_verified as boolean) ?? false,
    ...(token ? { session_token: token } : {}),
  } as User;

  if (token) {
    try {
      await AsyncStorage.setItem(AUTH_TOKEN_KEY, token);
      await AsyncStorage.setItem(CURRENT_USER_KEY, JSON.stringify(customerWithVerification));
      setLoginJustHappened();
    } catch {
      // ignore; zustand still has user
    }
  }
  getAuthStore().setUser(customerWithVerification);
  if (token) {
    scheduleFcmBackendSync('register');
  }
  return { ok: true, user: customerWithVerification };
}
